const ValidationHelper = {
  /**
   * Validates a Brazilian CPF (Individual Taxpayer ID)
   */
  CPF: (cpf) => {
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length !== 11 || /^(\d)\1{10}$/.test(cleanCPF)) return false;

    const digits = cleanCPF.split('').map(Number);
    const calculateCheckDigit = (factor) => {
      let sum = 0;
      for (let i = 0; i < factor - 1; i++) {
        sum += digits[i] * (factor - i);
      }
      const remainder = (sum * 10) % 11;
      return remainder >= 10 ? 0 : remainder;
    };

    return calculateCheckDigit(10) === digits[9] && calculateCheckDigit(11) === digits[10];
  },

  /**
   * Validates a Brazilian CNPJ (Corporate Taxpayer ID).
   *
   * Supports both the legacy numeric format and the new alphanumeric format
   * (effective July 2026): the first 12 positions may contain letters (A-Z)
   * and digits (0-9), while the last 2 check digits remain numeric.
   *
   * The check-digit algorithm is the traditional mod-11, but each character's
   * value is its ASCII code minus 48 ('0'-'9' → 0-9, 'A'-'Z' → 17-42).
   */
  CNPJ: (cnpj) => {
    // Remove mask, keep only alphanumerics and normalize letters to uppercase
    const cleanCNPJ = cnpj.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    // 14 positions: 12 alphanumeric + 2 numeric check digits
    if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(cleanCNPJ)) return false;

    // Reject trivially-invalid repeated sequences (e.g. 00000000000000)
    if (/^(.)\1{13}$/.test(cleanCNPJ)) return false;

    // Character value = ASCII code - 48
    const values = cleanCNPJ.split('').map((c) => c.charCodeAt(0) - 48);

    const calculateCheckDigit = (slice) => {
      let factor = slice.length - 7;
      let sum = 0;

      for (let i = slice.length; i >= 1; i--) {
        const n = slice[slice.length - i];
        sum += n * factor--;
        if (factor < 2) factor = 9;
      }

      const remainder = sum % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };

    const firstDigit = calculateCheckDigit(values.slice(0, 12));
    const secondDigit = calculateCheckDigit(values.slice(0, 13));

    return firstDigit === values[12] && secondDigit === values[13];
  },

  passport: (passport, country) => {
    
    const passportFormats = {
      
      US: /^[A-Z0-9]{9}$/,
      CA: /^[A-Z]{2}\d{6}$/,
      GB: /^\d{9}$/,
      FR: /^\d{2}[A-Z]{2}\d{5}$/,
      DE: /^[A-Z0-9]{9}$/,
      ES: /^[A-Z]{3}\d{6}$/,
      IT: /^[A-Z]{2}\d{7}$/,
      PT: /^[A-Z]\d{6}$/,
      IN: /^[A-Z]\d{7}$/,
      AU: /^[A-Z]\d{7}$/,
      RU: /^\d{9}$/,
      KR: /^[A-Z]\d{8}$/,

      // latam
      AR: /^[A-Z]{3}\d{6}$/,
      MX: /^[A-Z]\d{8}$/,
      BR: /^[A-Z]{2}\d{6}$/,
      CL: /^[A-Z]\d{7}$/,
      UY: /^[A-Z]{2}\d{6}$/,
      PY: /^[A-Z]\d{7}$/,
      BO: /^[A-Z]{2}\d{6}$/,
      PE: /^[A-Z]{2}\d{7}$/,
      CO: /^[A-Z]{2}\d{7}$/,
      VE: /^[A-Z]{2}\d{6}$/,
      EC: /^[A-Z]{2}\d{6}$/,
      CR: /^[A-Z]\d{8}$/,
      PA: /^[A-Z]\d{7}$/,
      GT: /^[A-Z]{2}\d{6}$/,
      HN: /^[A-Z]\d{7}$/,
      NI: /^[A-Z]\d{7}$/,
      SV: /^[A-Z]\d{7}$/,
      DO: /^[A-Z]{2}\d{6}$/,

      // Ásia
      CN: /^[EGP]\d{8}$/, 
      JP: /^[A-Z]{2}\d{7}$/,

      // África
      ZA: /^[A-Z0-9]{9}$/,
      NG: /^[A-Z]{2}\d{7}$/,
      EG: /^[A-Z]\d{8}$/,
      MA: /^[A-Z]{2}\d{6}$/,
      TN: /^[A-Z]{2}\d{6}$/,
      DZ: /^[A-Z]{2}\d{6}$/,
      KE: /^[A-Z]\d{7}$/,
      GH: /^[A-Z]\d{7}$/,
      UG: /^[A-Z]\d{7}$/,
      TZ: /^[A-Z]\d{7}$/,
      ET: /^[A-Z]\d{7}$/,
      RW: /^[A-Z]\d{7}$/,
      SN: /^[A-Z]{2}\d{6}$/,
      CI: /^[A-Z]{2}\d{6}$/,
      CM: /^[A-Z]{2}\d{6}$/,
      ZW: /^[A-Z]{2}\d{6}$/
    };

    const regex = passportFormats[country];

    if (!regex) return true; 

    return regex.test(passport.toUpperCase());
  }
};

export default ValidationHelper;